import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CalendarClock,
  CalendarDays,
  Users,
  Bus,
  CalendarRange,
  ClipboardCheck,
  ChevronRight,
} from 'lucide-react';

/**
 * ScheduleLanding — Phase 1 (reduced scope, 6 cards).
 *
 * Purpose:
 *   Hybrid landing dashboard for the Participant → Schedule parent tab.
 *   Navigation + safe read-only summary only. NOT a second calendar / roster /
 *   appointment / outcome engine.
 *
 * Cards (in mandated order):
 *   1. Rosters
 *   2. Appointments
 *   3. Activities (label rename from Group Sessions; underlying id unchanged)
 *   4. Transport Bookings
 *   5. Calendar
 *   6. Appointment Outcomes
 *
 * Deferred (NOT in DOM under any circumstance):
 *   - Staff Assigned (formal-vs-rostered semantics DEFERRED behind parked
 *     Assignment-Window Policy)
 *
 * Data source:
 *   Reads only from the existing GET /api/participants/{id}/overview payload
 *   (single fetch). Per-card summaries are safe read-only counts. Card clicks
 *   deep-link to the authoritative existing leaves via `onOpenLeaf`.
 *
 * Feature-flag governance:
 *   Rendered only when `participant_schedule_landing_enabled` is ON. This flag
 *   controls navigation visibility only; every destination inherits its own
 *   RBAC (roster.view, appointments.view, group_sessions.view, transport.view_*,
 *   participants.view).
 */

export function ScheduleLanding({ participantId, onOpenLeaf }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setDenied(false);
    api.get(`/participants/${participantId}/overview`)
      .then((r) => { if (!cancelled) setOverview(r?.data?.data || null); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) setDenied(true);
        else setError('Unable to load Schedule summary.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  // Safe summaries derived from existing overview payload only.
  const upcomingCount = useMemo(() => {
    const u = overview?.upcomingEvents;
    if (Array.isArray(u)) return u.length;
    if (u && Array.isArray(u.items)) return u.items.length;
    return null;
  }, [overview]);

  const assignedTeamCount = useMemo(() => {
    const t = overview?.assignedTeam;
    if (Array.isArray(t)) return t.length;
    if (t && Array.isArray(t.members)) return t.members.length;
    return null;
  }, [overview]);

  if (denied) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="schedule-landing-denied">
        You do not have permission to view Schedule for this participant.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="schedule-landing-loading">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="h-32 animate-pulse bg-slate-100/60" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-sm text-red-600" data-testid="schedule-landing-error">{error}</div>;
  }

  // 6 cards in mandated order. Staff Assigned intentionally OMITTED from DOM.
  const cards = [
    {
      id: 'rosters',
      testId: 'schedule-card-rosters',
      title: 'Rosters',
      icon: CalendarClock,
      description: 'Upcoming and recent shifts for this participant (read-only).',
      leaf: 'rosters',
      summary: assignedTeamCount != null ? (
        <span className="text-xs text-muted-foreground" data-testid="schedule-card-rosters-summary">
          Assigned team: <span className="font-medium text-foreground">{assignedTeamCount}</span>
        </span>
      ) : null,
      actionLabel: 'Open Rosters',
    },
    {
      id: 'appointments',
      testId: 'schedule-card-appointments',
      title: 'Appointments',
      icon: CalendarDays,
      description: 'Scheduled appointments and their status.',
      leaf: 'appointments',
      actionLabel: 'Open Appointments',
    },
    {
      id: 'activities',
      testId: 'schedule-card-activities',
      title: 'Activities',
      icon: Users,
      description: 'Linked activities (group and centre-based sessions).',
      leaf: 'group-sessions',
      actionLabel: 'Open Activities',
    },
    {
      id: 'transport',
      testId: 'schedule-card-transport',
      title: 'Transport Bookings',
      icon: Bus,
      description: 'Operational transport trips linked to this participant.',
      leaf: 'transport',
      actionLabel: 'Open Transport',
    },
    {
      id: 'calendar',
      testId: 'schedule-card-calendar',
      title: 'Calendar',
      icon: CalendarRange,
      description: 'Chronological view of shifts, appointments and activities.',
      leaf: 'calendar',
      summary: upcomingCount != null ? (
        <span className="text-xs text-muted-foreground" data-testid="schedule-card-calendar-summary">
          Upcoming events: <span className="font-medium text-foreground">{upcomingCount}</span>
        </span>
      ) : null,
      actionLabel: 'Open Calendar',
    },
    {
      id: 'appointment-outcomes',
      testId: 'schedule-card-appointment-outcomes',
      title: 'Appointment Outcomes',
      icon: ClipboardCheck,
      description: 'Completed appointments — distinct from Goal / Participant Outcomes.',
      leaf: 'appointment-outcomes',
      actionLabel: 'Open Outcomes',
    },
  ];

  return (
    <div className="space-y-4" data-testid="schedule-landing">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card
              key={c.id}
              data-testid={c.testId}
              className="flex flex-col hover:border-primary/60 hover:shadow-sm cursor-pointer transition-all"
              onClick={() => onOpenLeaf(c.leaf)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenLeaf(c.leaf);
                }
              }}
            >
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {Icon && <Icon className="w-4 h-4 text-primary shrink-0" />}
                  <CardTitle className="text-sm font-semibold truncate">{c.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="text-sm flex-1 flex flex-col justify-between gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">{c.description}</p>
                  {c.summary}
                </div>
                <div
                  className="flex items-center justify-end text-xs text-primary font-medium"
                  data-testid={`${c.testId}-action`}
                >
                  {c.actionLabel} <ChevronRight className="w-3 h-3 ml-1" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground italic" data-testid="schedule-landing-footer">
        Worker-to-participant assignment is deferred pending the Assignment-Window Policy
        review and is intentionally not surfaced here.
      </p>
    </div>
  );
}

export default ScheduleLanding;
